/**
 * 页面 chunk 预取。
 *
 * 路由是**按页分包**的，而 react-router 的 route-level `lazy` 会先 await 这个 chunk、
 * 加载完才提交导航并渲染（见 `router/index.tsx` 的 `load`）。所以第一次点进一页时，
 * 用户要等「下载 chunk + 执行 + 首次渲染」这一整段，在 220ms RTT 的链路上实测约 870ms——
 * 比这页所有接口加起来还多。
 *
 * 这里把每个页面的 `import()` 按**绝对路径**登记下来，菜单悬停时提前拉。命中时那 1–2 个
 * RTT 已经被移到鼠标移上去到点下去之间，用户点下去时 chunk 已在模块表里。
 *
 * 三条纪律：
 * - 只是提前量：**没预取到也一定不影响导航**，真导航时路由自己会再 import 一次
 * - 失败静默：不弹提示、不写日志（否则一次网络抖动会给用户一条看不懂的错误）
 * - 不做缓存：`started` 只防同一页面被反复触发，模块本身由打包器/浏览器缓存
 */
import type { RouteObject } from 'react-router';

/** 一次页面 chunk 的预取：就是路由里那个 `() => import('@/pages/...')` */
export type RoutePreloader = () => Promise<unknown>;

/** 绝对路径 → 预取函数；由 {@link collectRoutePreloaders} 在路由模块加载时一次登记完 */
const preloaders = new Map<string, RoutePreloader>();

/** 已经发起过预取的路径。失败会撤掉，下次悬停还能再试 */
const started = new Set<string>();

/**
 * 归一化：菜单给的是站内路径，可能带查询串或尾斜杠；通配路由不参与预取。
 * 认不出是页面的（空、`*`、`group::xxx` 这类内部键）一律返回 undefined。
 */
function normalizePath(path: string | undefined): string | undefined {
  if (!path) return undefined;
  const clean = path.split('?')[0].split('#')[0];
  if (!clean || clean.includes('*') || !clean.startsWith('/')) return undefined;
  const withoutTrailing = clean.length > 1 ? clean.replace(/\/+$/, '') : clean;
  return withoutTrailing || undefined;
}

export function registerRoutePreloader(
  path: string | undefined,
  preloader: RoutePreloader,
): void {
  const key = normalizePath(path);
  if (!key) return;
  preloaders.set(key, preloader);
}

/**
 * 预取某个站内路由的页面 chunk。
 *
 * 菜单的悬停回调调它；没登记、已经预取过、或路径不像页面时**直接返回**。
 * 失败不冒泡——预取只是提前量，真导航时路由自己会再取一次并走正常的失败路径。
 */
export function preloadRoute(path: string | undefined): void {
  const key = normalizePath(path);
  if (!key || started.has(key)) return;
  const preloader = preloaders.get(key);
  if (!preloader) return;
  started.add(key);
  preloader().catch(() => {
    // 这次没拉到就不算预取过：下一次悬停再试，别把一个页面永久钉死
    started.delete(key);
  });
}

/** 路由表里能被预取的形状：`lazy` 上挂着 `prefetch`（见 `router/index.tsx` 的 `load`） */
interface PrefetchableRouteLike {
  path?: string;
  children?: PrefetchableRouteLike[];
  lazy?: unknown;
}

function joinPath(parent: string, path: string | undefined): string {
  if (!path) return parent;
  if (path.startsWith('/')) return path;
  if (!parent || parent === '/') return `/${path}`;
  return `${parent}/${path}`;
}

/**
 * 把静态路由树里每个页面的预取函数按**绝对路径**登记进表。
 *
 * 登记要绝对路径，是因为菜单（`useMenu`）手里只有 `MENU_UI` 的 `path`，
 * 没有父级上下文；而路由表里除根以外都是相对路径，所以这里按树拼一次。
 * `index` 路由（无 path）与 `element` 路由不会被登记。
 */
export function collectRoutePreloaders(
  routes: readonly PrefetchableRouteLike[],
  register: (path: string, preloader: RoutePreloader) => void = registerRoutePreloader,
  parentPath = '',
): void {
  routes.forEach((route) => {
    const full = joinPath(parentPath, route.path);
    const prefetch = (route.lazy as { prefetch?: RoutePreloader } | undefined)
      ?.prefetch;
    // 通配路由（`*`、`403` 之类没有页面路径的）不预取：菜单里不会悬停到它
    if (prefetch && full && !full.includes('*')) register(full, prefetch);
    if (route.children?.length) {
      collectRoutePreloaders(route.children, register, full);
    }
  });
}

/**
 * 路由模块加载时调一次：`collectRoutePreloaders(routes)`。
 * 单独导出这层包装是为了让 `RouteObject[]` 能直接传进来（上面那个泛型收口在测试里用）。
 */
export function registerRouteTree(routes: readonly RouteObject[]): void {
  collectRoutePreloaders(routes as readonly PrefetchableRouteLike[]);
}
